import { useState, useEffect } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase'
import { 
  ContentSection, 
  ContentSectionType, 
  HeroContent, 
  AboutContent, 
  ContactContent, 
  PricingContent,
  HeaderContent,
  FooterContent,
  VideoSectionContent,
  SiteSettings,
  TestimonialsContent
} from '../types/content'
import { ORIGINAL_SITE_SETTINGS } from '../constants/designSystem'

export const usePublicContent = (sectionType: ContentSectionType) => {
  const [content, setContent] = useState<ContentSection | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!sectionType) return

    const docRef = doc(db, 'content', sectionType)
    
    // Set up real-time listener for public content
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setContent({
            id: snapshot.id,
            section: sectionType,
            data: data.data,
            lastModified: data.lastModified?.toDate() || new Date(),
            modifiedBy: data.modifiedBy || 'Unknown',
            version: data.version || 1
          })
        } else {
          // Use default content if no Firestore data exists
          setContent(createDefaultPublicContent(sectionType))
        }
        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Error fetching public content:', error)
        // Fallback to default content on error
        setContent(createDefaultPublicContent(sectionType))
        setLoading(false)
        setError('Using default content')
      }
    )

    return () => unsubscribe()
  }, [sectionType])

  return {
    content,
    loading,
    error
  }
}

// Default content fallback (same as admin, but for public use)
const createDefaultPublicContent = (sectionType: ContentSectionType): ContentSection => {
  const baseContent = {
    id: sectionType,
    section: sectionType,
    lastModified: new Date(),
    modifiedBy: 'System',
    version: 1
  }

  switch (sectionType) {
    case 'hero':
      return {
        ...baseContent,
        section: 'hero',
        data: {
          title: 'Perfect Music for',
          subtitle: 'Every Event',
          description: 'From acoustic duos to full bands, we make every event unforgettable.',
          videoUrl: 'https://www.youtube.com/embed/9bZkp7q19f0?autoplay=1&mute=1&loop=1&playlist=9bZkp7q19f0&controls=0&showinfo=0&rel=0&modestbranding=1&start=0&end=212&iv_load_policy=3&fs=0&cc_load_policy=0&vq=hd1080',
          stats: [
            { number: '500+', label: 'Events Booked' },
            { number: '200+', label: 'Musicians Available' },
            { number: '100+', label: 'Venues Served' }
          ],
          clickableText: 'Bespoke Curation for Live Events',
          ctaButtonText: 'Get in Touch',
          // Legacy fields for backward compatibility
          primaryButtonText: 'Book Live Music',
          secondaryButtonText: 'Browse Musicians'
        } as HeroContent
      }

    case 'about':
      return {
        ...baseContent,
        section: 'about',
        data: {
          title: 'Perfect Music for',
          subtitle: 'Every Occasion',
          description: 'For over a decade, O-Live has been connecting venues and event planners with the perfect musicians. From intimate acoustic sets to full band performances, we make every event memorable with the right music at the right time.',
          stats: [
            { number: '500+', label: 'Events Booked' },
            { number: '200+', label: 'Musicians Available' },
            { number: '100+', label: 'Venues Served' }
          ],
          ctaButtonText: 'Get in Touch',
          venueLogos: []
        } as AboutContent
      }

    case 'contact':
      return {
        ...baseContent,
        section: 'contact',
        data: {
          title: 'Book Live Music for',
          subtitle: 'Your Event',
          description: 'Ready to add live music to your event? Get in touch with us to discuss your needs, browse available musicians, or book the perfect performers for your occasion.',
          contactInfo: {
            email: ['ollie@o-live.co.uk', 'booking@o-live.com'],
            phone: ['+44 161 123 4567', '+44 161 987 6543'],
            address: ['123 Music Street', 'Manchester, M1 1AA, UK'],
            hours: ['Mon-Fri: 9AM-6PM GMT', 'Weekend: By Appointment']
          },
          socialLinks: {
            instagram: '',
            twitter: '',
            linkedin: '',
            spotify: '',
            tiktok: ''
          },
          serviceTypes: [
            'Wedding Music',
            'Corporate Event',
            'Bar/Restaurant Booking',
            'Private Party',
            'Venue Partnership',
            'Music Consultation',
            'Other'
          ]
        } as ContactContent
      }

    case 'pricing':
      return {
        ...baseContent,
        section: 'pricing',
        data: {
          title: `Get Our ${new Date().getFullYear()} Pricing Guide`,
          description: 'Enter your email and we\'ll send you our complete pricing information for all booking services, special packages, and venue partnership rates.',
          emailPlaceholder: 'your@email.com',
          buttonText: 'Send Pricing Guide',
          successTitle: 'Pricing Guide Sent!',
          successDescription: 'Check your email for the complete pricing guide. It should arrive within a few minutes.',
          footerText: 'We\'ll email you the pricing guide and keep you updated on our latest rates and availability.'
        } as PricingContent
      }

    case 'header':
      return {
        ...baseContent,
        section: 'header',
        data: {
          logo: '/assets/LOGO - TRANSPARENT WHITE.png',
          logoAlt: 'O-Live Music Booking',
          navigation: [
            { name: 'Home', href: '#home', order: 1 },
            { name: 'About', href: '#about', order: 2 },
            { name: 'Artists', href: '#artists', order: 3 },
            { name: 'Pricing', href: '#pricing', order: 4 },
            { name: 'Contact', href: '#contact', order: 5 }
          ],
          ctaButtonText: 'Get in Touch'
        } as HeaderContent
      }

    case 'footer':
      return {
        ...baseContent,
        section: 'footer',
        data: {
          logo: '/assets/LOGO - TRANSPARENT WHITE.png',
          logoAlt: 'O-Live Music Booking',
          companyDescription: 'Elevating artists and creating unforgettable musical experiences. We\'re passionate about connecting extraordinary talent with audiences who appreciate authentic artistry.',
          contactInfo: {
            email: ['ollie@o-live.co.uk'],
            phone: ['+44 161 123 4567'],
            address: ['Manchester, UK'],
            hours: []
          },
          socialLinks: [
            { platform: 'Instagram', url: '', icon: 'FiInstagram' },
            { platform: 'Twitter', url: '', icon: 'FiTwitter' },
            { platform: 'LinkedIn', url: '', icon: 'FiLinkedin' },
            { platform: 'Facebook', url: '', icon: 'FiFacebook' }
          ],
          quickLinks: [
            { name: 'About Us', href: '#about', order: 1 },
            { name: 'Our Artists', href: '#artists', order: 2 },
            { name: 'Pricing', href: '#pricing', order: 3 },
            { name: 'Contact', href: '#contact', order: 4 }
          ],
          copyrightText: `© ${new Date().getFullYear()} O-Live Music Agency.`
        } as FooterContent
      }

    case 'videos':
      return {
        ...baseContent,
        section: 'videos',
        data: {
          title: 'Live',
          subtitle: 'Performances',
          description: 'Watch Big Swing Energy in action at exclusive venues across the country. Experience the energy and talent that makes every performance unforgettable.',
          videos: [
            {
              id: '1',
              title: 'Easy Lover',
              artist: 'Big Swing Energy',
              poster: '/assets/easy-lover-poster.jpg',
              videoUrl: '/assets/easy-lover.mp4',
              runtime: '3:45',
              description: '',
              order: 1
            },
            {
              id: '2',
              title: 'Live Performance',
              artist: 'Big Swing Energy',
              poster: '/assets/LOGO - BANNER.png',
              videoUrl: '/assets/easy-lover.mp4',
              runtime: '4:20',
              description: '',
              order: 2
            }
          ]
        } as VideoSectionContent
      }

    case 'site-settings':
      return {
        ...baseContent,
        section: 'site-settings',
        data: ORIGINAL_SITE_SETTINGS as SiteSettings
      }

    case 'testimonials':
      return {
        ...baseContent,
        section: 'testimonials',
        data: {
          title: 'What Our Clients Say',
          subtitle: 'Hear from our satisfied clients',
          testimonials: [
            {
              id: '1',
              reviewerName: 'Sarah Johnson',
              rating: 5,
              reviewText: 'O-Live provided the perfect band for our wedding. The musicians were professional, talented, and really made our special day unforgettable. Highly recommend!',
              date: 'January 2024',
              order: 1
            },
            {
              id: '2',
              reviewerName: 'Michael Chen',
              rating: 5,
              reviewText: 'Absolutely fantastic service! They helped us find the ideal acoustic duo for our corporate event. The attention to detail and professionalism was outstanding.',
              date: 'December 2023',
              order: 2
            },
            {
              id: '3',
              reviewerName: 'Emma Williams',
              rating: 5,
              reviewText: 'Working with O-Live was a breeze. They understood exactly what we needed for our venue and delivered exceptional musicians every time. A true pleasure to work with.',
              date: 'November 2023',
              order: 3
            },
            {
              id: '4',
              reviewerName: 'David Thompson',
              rating: 5,
              reviewText: 'The jazz quartet they provided for our private event was incredible. Guests are still talking about it! Professional, punctual, and extremely talented.',
              date: 'October 2023',
              order: 4
            }
          ]
        } as TestimonialsContent
      }

    default:
      throw new Error(`Unknown section type: ${sectionType}`)
  }
}
